// The S-2 estimate seam: three typed judgments about the enemy, behind one interface, with two
// providers.
//
// **The pattern.** These are "smart if statements". Each one is a question ordinary code cannot answer
// well — where is the enemy weighting his defence, what is this contact, is the reserve about to move —
// and each one returns a *type the interface already knows how to render*: a named third, a role, a
// yes/no, each with a probability distribution and a confidence. No prose, ever. That is what lets the
// provider be swapped without a single change in the UI, and it is why the deterministic provider is
// the default rather than a mock: the heuristic is a real answer, not a placeholder.
//
// **Why the seam exists at all.** `docs/research/05-jev-evaluation.md` evaluated Jev (TypeSafe AI's
// System One model) and recommended exactly this: build the seam now, keep the heuristic as the
// default, and calibrate against the simulation before a player ever sees a number. The published
// independent work is the reason for that order — Jev's out-of-distribution expected calibration error
// is 0.107, and on a task whose answer is genuinely absent from the input it returned mean confidence
// 0.74 at 44.7% accuracy. "The answer is genuinely absent from the input" is a fair description of an
// S-2 estimate at H+5 with two stale contacts. The simulation is the unusual environment where that is
// fixable: it is deterministic, seeded, and generates unlimited labelled ground truth, so every
// question here can be recalibrated locally (`tools/jev-calibrate.mts`) and the interface can say
// whether it was.
//
// **What the interface does with the numbers.** Not this module's business, but worth stating: the
// existing S-2 surfaces show a confidence chip and a ring size, never a raw 0.87. The one place a
// percentage is shown to the player is the decision-point elicitation card, and there it is shown only
// *after* the player has committed to their own number (`src/ui/elicit/ElicitCard.tsx`), because a
// forecast shown first is an anchor, not a comparison.

import { calibrateChoice, calibrateNoul, type EstimatorQuestion } from './calibration';
import { confidenceOf } from './calibrationMath';
import type { CopContact, EstimatorContext, ThirdName } from './copContext';
import {
  contactWeightByThird, depthBandLabel, isPastBand, MANEUVER_TYPES, ownPastBandByThird,
  reserveTriggerBandId, thirdOf, thirdNameOf,
} from './copContext';
import { jevEnabled } from './jevFlag';
import { callJev, closeEstimateRow, openEstimateRow } from './jevClient';
import {
  buildJevState, CONTACT_ROLES, contactLabel, contactRoleQuestion, CONTACT_ROLE_QUESTION_ID,
  MAIN_EFFORT_QUESTION_ID, pictureQuestions, RESERVE_QUESTION_ID, stateKey, thirdOptions,
  type ContactRole,
} from './jevState';
import { TICKS_PER_MIN } from '../sim/types';

export type { ContactRole } from './jevState';
export type { ThirdName } from './copContext';

/** One typed judgment. `probabilities` always sums to 1 and always contains `String(value)` as a key. */
export interface Estimate<T> {
  value: T;
  probabilities: Record<string, number>;
  /** 0..1, TypeSafe's own formula: `(count x peak - 1) / (count - 1)`. Flat distribution, no confidence. */
  confidence: number;
  provider: 'heuristic' | 'jev';
  /** Whether a fitted map from `data/jev-calibration.json` was applied. Never claimed optimistically. */
  calibrated: boolean;
  latencyMs: number;
  /** Set when a Jev call was attempted and the heuristic answered instead. */
  fallbackReason?: string;
}

export interface Estimator {
  readonly name: 'heuristic' | 'jev';
  /** Which third of the corridor holds the enemy main effort. */
  mainEffortThird(ctx: EstimatorContext): Promise<Estimate<ThirdName>>;
  /** What one reported contact most likely is. */
  contactRole(ctx: EstimatorContext, contact: CopContact): Promise<Estimate<ContactRole>>;
  /** Whether the enemy commits its reserve within the next ten minutes. */
  reserveCommitSoon(ctx: EstimatorContext): Promise<Estimate<boolean>>;
}

/** The window "soon" means, in sim minutes. It is the window the calibration harness labels against and
 * the window the elicitation card asks about; all three have to agree or the Brier score is nonsense. */
export const RESERVE_WINDOW_MIN = 10;

// ---- shared arithmetic ---------------------------------------------------------------------------------

/**
 * Softmax with a floor.
 *
 * The floor matters more than the temperature. A heuristic that returns 0 for an option is claiming
 * certainty it has not earned, and an option at exactly 0 makes the Brier score unboundedly bad the
 * first time it happens — which, over four areas of operation and a few thousand fights, it will.
 */
function softmax(scores: Record<string, number>, temperature = 1, floor = 0.02): Record<string, number> {
  const entries = Object.entries(scores);
  if (entries.length === 0) return {};
  const max = Math.max(...entries.map(([, v]) => v));
  const exps = entries.map(([k, v]) => [k, Math.exp((v - max) / Math.max(1e-6, temperature))] as const);
  const total = exps.reduce((s, [, v]) => s + v, 0);
  const raw = exps.map(([k, v]) => [k, total > 0 ? v / total : 1 / entries.length] as const);
  // Mix with the uniform distribution rather than clamping, which would break normalisation.
  const uniform = 1 / entries.length;
  const mixed = raw.map(([k, v]) => [k, (1 - floor * entries.length) * v + floor * entries.length * uniform] as const);
  const mixedTotal = mixed.reduce((s, [, v]) => s + v, 0);
  return Object.fromEntries(mixed.map(([k, v]) => [k, v / mixedTotal]));
}

function argmaxOf<T extends string>(probabilities: Record<string, number>, options: readonly T[]): T {
  let best = options[0];
  let bestP = -Infinity;
  for (const option of options) {
    const p = probabilities[option] ?? 0;
    if (p > bestP || (p === bestP && option < best)) { best = option; bestP = p; }
  }
  return best;
}

function sigmoid(z: number): number {
  return 1 / (1 + Math.exp(-z));
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

// ---- the heuristic provider -------------------------------------------------------------------------

/**
 * How much a contact's freshness matters, in sim minutes of half-life. The detection model drops a
 * contact entirely after ten minutes unrefreshed, so a five-minute half-life puts a contact about to be
 * dropped at a quarter of the weight of one seen this minute.
 */
const CONTACT_HALF_LIFE_MIN = 5;

/** How much friction (a friendly platoon in contact) counts against a contact report, per third. Lower
 * than a contact because it is indirect evidence: being shot at says the enemy is there, not that he
 * has weighted his main effort there. */
const FRICTION_WEIGHT = 0.6;

/** Softmax temperature for the main-effort judgment. Tuned so that a two-to-one weight of evidence
 * produces roughly 55/25/20 rather than 90/5/5: a defender's main effort is a real judgment call and an
 * S-2 who is 90% sure at H+20 is an S-2 who has stopped looking. */
const MAIN_EFFORT_TEMPERATURE = 1.2;

/** Depth bonus: a contact deep in the enemy's own ground is better evidence of where he has weighted
 * than a screen element he pushed out to be seen. Expressed as a multiplier on the contact's weight. */
const DEPTH_BONUS = 0.5;

function mainEffortScores(ctx: EstimatorContext): Record<string, number> {
  const names = thirdOptions(ctx);
  const contactWeight = contactWeightByThird(ctx, CONTACT_HALF_LIFE_MIN);

  // A contact deeper in the corridor counts for more; `advanceDepth` is signed so bigger is deeper.
  const depths = ctx.frame.bands.map((b) => ctx.frame.depthSign * b.depth);
  const depthMin = depths.length > 0 ? Math.min(...depths) : 0;
  const depthMax = depths.length > 0 ? Math.max(...depths) : 1;
  const depthSpan = Math.max(1, depthMax - depthMin);

  const scores: Record<string, number> = {};
  for (const name of names) scores[name] = 0;

  for (const c of ctx.contacts) {
    const ageMin = Math.max(0, (ctx.tick - c.lastSeen) / TICKS_PER_MIN);
    const recency = Math.pow(0.5, ageMin / CONTACT_HALF_LIFE_MIN);
    const d = ctx.frame.depthSign * (ctx.frame.depthAxis === 'x' ? c.x : c.y);
    const depthFrac = Math.min(1, Math.max(0, (d - depthMin) / depthSpan));
    scores[thirdNameOf(ctx.frame, c)] += recency * (1 + DEPTH_BONUS * depthFrac);
  }

  for (const u of ctx.own) {
    if (!u.alive || !u.inContact || !MANEUVER_TYPES.has(u.type)) continue;
    scores[thirdNameOf(ctx.frame, u)] += FRICTION_WEIGHT;
  }

  // The briefed posture as a prior, per `dials.red`. 'forward' means the enemy is massed on the first
  // phase line across the whole frontage, which flattens the judgment rather than pointing it anywhere;
  // it is applied by damping the evidence, not by favouring a third.
  if (ctx.dials.red === 'forward') {
    for (const name of names) scores[name] *= 0.6;
  }

  // With no evidence at all every third scores 0 and the softmax returns uniform, which is the honest
  // answer at H+0 and is what `confidenceOf` will read as no confidence.
  void contactWeight;
  return scores;
}

function heuristicMainEffort(ctx: EstimatorContext): { probabilities: Record<string, number>; value: ThirdName } {
  const names = thirdOptions(ctx);
  const probabilities = softmax(mainEffortScores(ctx), MAIN_EFFORT_TEMPERATURE);
  return { probabilities, value: argmaxOf<ThirdName>(probabilities, names) };
}

/** Softmax temperature for the contact-role judgment: the evidence is weaker and more often absent, so
 * a flatter answer is the calibrated one. */
const ROLE_TEMPERATURE = 1.4;

function heuristicContactRoleScores(ctx: EstimatorContext, contact: CopContact): Record<ContactRole, number> {
  const scores: Record<ContactRole, number> = { screen: 0, mainBody: 0, reserve: 0, unknown: 0 };

  // 1. Where it sits, relative to the phase lines. Deeper is the enemy's own rear, which is where a
  //    reserve waits; forward of the first line is where a screen is pushed out to see and delay.
  const pls = ctx.frame.bands.filter((b) => b.kind === 'pl');
  const band = depthBandLabel(ctx.frame, contact);
  if (pls.length > 0) {
    const forwardOfFirst = !isPastBand(ctx.frame, contact, pls[0].id);
    const pastSecond = pls.length > 1 && isPastBand(ctx.frame, contact, pls[1].id);
    const pastThird = pls.length > 2 && isPastBand(ctx.frame, contact, pls[2].id);
    if (forwardOfFirst) { scores.screen += 1.4; scores.mainBody += 0.2; }
    else if (!pastSecond) { scores.mainBody += 1.2; scores.screen += 0.4; }
    else if (!pastThird) { scores.mainBody += 0.8; scores.reserve += 0.8; }
    else { scores.reserve += 1.6; scores.mainBody += 0.2; }
  }

  // 2. What the observer could make of it. The reported type is a guess and is often wrong, so it moves
  //    the estimate rather than deciding it — and 'unknown'/'vehicle' are observation limits, which is
  //    evidence for the no-match outcome and for nothing else.
  switch (contact.typeConf) {
    case 'recon': scores.screen += 1.2; break;
    case 'armor': scores.reserve += 0.9; scores.mainBody += 0.4; break;
    case 'mech': case 'inf': scores.mainBody += 1.0; break;
    case 'spArty': case 'mortar': scores.reserve += 0.5; scores.mainBody += 0.3; break;
    case 'hq': case 'log': scores.reserve += 0.6; scores.unknown += 0.3; break;
    case 'uas': scores.screen += 0.6; break;
    case 'vehicle': scores.unknown += 0.5; break;
    case 'unknown': scores.unknown += 1.1; break;
    default: break;
  }

  // 3. When it was first reported. A screen is what an attack meets first; something first seen half an
  //    hour into the fight, well back, is a reserve that has only now been looked at.
  const firstSeenMin = contact.firstSeen / TICKS_PER_MIN;
  if (firstSeenMin <= 10) scores.screen += 0.5;
  else if (firstSeenMin >= 25) scores.reserve += 0.6;

  // 4. How good the report is. A stale contact with a wide error radius is not evidence of a role.
  const staleMin = Math.max(0, (ctx.tick - contact.lastSeen) / TICKS_PER_MIN);
  if (staleMin >= 6) scores.unknown += 0.8;
  if (contact.errorM >= 400) scores.unknown += 0.5;

  // 5. Company. A contact alone in its third is more likely a screen or an outpost; three together are
  //    a main body. This is the one piece of context the single-contact fields cannot carry.
  const sameThird = ctx.contacts.filter((c) => thirdOf(ctx.frame, c) === thirdOf(ctx.frame, contact)).length;
  if (sameThird >= 3) scores.mainBody += 0.6;
  else if (sameThird <= 1) scores.screen += 0.3;

  void band;
  return scores;
}

function heuristicContactRole(
  ctx: EstimatorContext, contact: CopContact,
): { probabilities: Record<string, number>; value: ContactRole } {
  const probabilities = softmax(heuristicContactRoleScores(ctx, contact), ROLE_TEMPERATURE);
  return { probabilities, value: argmaxOf<ContactRole>(probabilities, CONTACT_ROLES) };
}

/**
 * The reserve judgment.
 *
 * The scripted red cell commits its reserve when two detected blue platoons are past PL WHITE in one
 * third (`src/sim/redcell.ts`, `stepReserve`), and under the 'forward' posture it throws its whole
 * force when two are past PL RED. A heuristic that counts blue's own platoons past that line is
 * therefore reading the same gauge red reads — but through red's fog, not blue's: red only sees what
 * its own detection rolls have given it, and its reports arrive late. That gap is the whole uncertainty
 * in this question, and it is why the answer is a probability rather than a lookup.
 */
function heuristicReserveP(ctx: EstimatorContext): number {
  const posture = ctx.dials.red;

  // 'screen' has no reserve-commitment behaviour at all: it delays backwards under pressure. The floor
  // is not zero because "the enemy does something that looks like a counterattack" is still possible.
  if (posture === 'screen') return 0.06;

  const pls = ctx.frame.bands.filter((b) => b.kind === 'pl');
  const triggerId = posture === 'forward' ? pls[0]?.id : reserveTriggerBandId(ctx.frame);
  if (!triggerId) return 0.12;

  const past = ownPastBandByThird(ctx, triggerId);
  const maxPast = Math.max(...past);

  // Logistic in the count, centred just under the threshold of two: at one platoon across the decision
  // is genuinely open, at two it is imminent but red has not necessarily seen it yet, at three it is
  // near certain that red has the picture it needs.
  let p = sigmoid(2.2 * (maxPast - 1.6));

  // Detection and report latency: the count above is ground truth about blue, but red is working from
  // reports that are minutes old under jamming. Heavy electronic warfare pushes the commitment later.
  if (ctx.dials.ew === 'heavy') p *= 0.75;
  else if (ctx.dials.ew === 'moderate') p *= 0.88;
  if (ctx.dials.tod === 'night') p *= 0.85;

  // 'forward' commits everything, once, early — so the probability rises faster and falls away sooner.
  if (posture === 'forward') p = Math.min(0.95, p * 1.15);

  // It is a one-shot decision. If the threshold has been met for a long time, the commitment has most
  // likely already happened, and the question is about a *new* commitment in the next ten minutes.
  // Blue cannot observe that directly, so this is a decay on elapsed time past the trigger, not a flag.
  const crossings = ctx.own.filter(
    (u) => u.alive && MANEUVER_TYPES.has(u.type) && isPastBand(ctx.frame, u, triggerId),
  ).length;
  if (crossings >= 2) {
    const minutesSincePhase = Math.max(0, ctx.elapsedMin - 20);
    p *= Math.pow(0.97, minutesSincePhase);
  }

  return Math.min(0.97, Math.max(0.03, p));
}

// ---- the two providers ---------------------------------------------------------------------------------

function finishChoice<T extends string>(
  question: EstimatorQuestion,
  provider: 'heuristic' | 'jev',
  probabilities: Record<string, number>,
  options: readonly T[],
  startedAt: number,
  fallbackReason?: string,
): Estimate<T> {
  const cal = calibrateChoice(question, provider, probabilities);
  return {
    value: argmaxOf(cal.probabilities, options),
    probabilities: cal.probabilities,
    confidence: cal.confidence,
    provider,
    calibrated: cal.calibrated,
    latencyMs: Math.round(now() - startedAt),
    fallbackReason,
  };
}

function finishNoul(
  question: EstimatorQuestion,
  provider: 'heuristic' | 'jev',
  p: number,
  startedAt: number,
  fallbackReason?: string,
): Estimate<boolean> {
  const cal = calibrateNoul(question, provider, p);
  return {
    value: cal.p >= 0.5,
    probabilities: { yes: cal.p, no: 1 - cal.p },
    confidence: cal.confidence,
    provider,
    calibrated: cal.calibrated,
    latencyMs: Math.round(now() - startedAt),
    fallbackReason,
  };
}

/**
 * The deterministic provider: pure functions over the common operating picture.
 *
 * Deterministic in the strict sense — the same context always produces the same distribution, with no
 * clock, no random number generator and no network — which is what `tests/ai/estimator.test.ts`
 * asserts and what makes it usable as the floor under a model that may be slow or absent.
 */
export const heuristicEstimator: Estimator = {
  name: 'heuristic',

  async mainEffortThird(ctx) {
    const started = now();
    const { probabilities } = heuristicMainEffort(ctx);
    return finishChoice('mainEffortThird', 'heuristic', probabilities, thirdOptions(ctx), started);
  },

  async contactRole(ctx, contact) {
    const started = now();
    const { probabilities } = heuristicContactRole(ctx, contact);
    return finishChoice('contactRole', 'heuristic', probabilities, CONTACT_ROLES, started);
  },

  async reserveCommitSoon(ctx) {
    const started = now();
    return finishNoul('reserveCommitSoon', 'heuristic', heuristicReserveP(ctx), started);
  },
};

/** Synchronous access to the heuristic's raw numbers, for the calibration harness and for tests that
 * want to assert determinism without awaiting three promises. Not part of the `Estimator` interface. */
export const heuristicRaw = {
  mainEffortThird: heuristicMainEffort,
  contactRole: heuristicContactRole,
  reserveCommitSoon: heuristicReserveP,
};

/** Normalises a Jev distribution onto exactly the options we asked about: an option the model omitted
 * becomes 0 (before the floor), and an option it invented is dropped. Typed output guarantees the
 * interface, not the contents — the docs say so, and this is what acting on that looks like. */
function restrictTo<T extends string>(
  probabilities: Record<string, number>, options: readonly T[],
): Record<string, number> {
  const out: Record<string, number> = {};
  let total = 0;
  for (const option of options) {
    const v = probabilities[option];
    const p = typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0;
    out[option] = p;
    total += p;
  }
  if (total <= 0) {
    const uniform = 1 / options.length;
    for (const option of options) out[option] = uniform;
    return out;
  }
  for (const option of options) out[option] /= total;
  return out;
}

export interface JevEstimatorOptions {
  /** Injectable for tests; defaults to the module-level `callJev`. */
  call?: typeof callJev;
  /** Set false in tests that do not want audit rows. */
  audit?: boolean;
}

/**
 * Builds the Jev-backed provider.
 *
 * Every method falls back to the heuristic on any failure — no key, a timeout, a 429, an answer whose
 * options do not match the question — and the returned estimate says `provider: 'heuristic'` with the
 * reason attached, so nothing downstream can claim a model answer it did not get.
 */
export function createJevEstimator(opts: JevEstimatorOptions = {}): Estimator {
  const call = opts.call ?? callJev;
  const withAudit = opts.audit !== false;

  const fallback = async <T>(
    run: () => Promise<Estimate<T>>, reason: string, auditId: number | undefined,
  ): Promise<Estimate<T>> => {
    const estimate = await run();
    const out = { ...estimate, fallbackReason: reason };
    if (auditId !== undefined) {
      closeEstimateRow(auditId, {
        provider: 'heuristic',
        latencyMs: out.latencyMs,
        confidence: out.confidence,
        calibrated: out.calibrated,
        error: reason,
      });
    }
    return out;
  };

  return {
    name: 'jev',

    async mainEffortThird(ctx) {
      const started = now();
      const state = buildJevState(ctx);
      const questions = pictureQuestions(ctx);
      const key = stateKey(ctx);
      const auditId = withAudit
        ? openEstimateRow('mainEffortThird', key.length, `H+${Math.round(ctx.elapsedMin)} picture`)
        : undefined;

      const res = await call(state, questions, {
        question: 'mainEffortThird',
        ref: `H+${Math.round(ctx.elapsedMin)}`,
        cacheKey: `picture\u0000${key}`,
      });
      const answer = res.ok ? res.answers[MAIN_EFFORT_QUESTION_ID] : undefined;
      if (!answer || answer.type !== 'choice') {
        return fallback(
          () => heuristicEstimator.mainEffortThird(ctx),
          res.error ?? 'jev: no usable main-effort answer',
          auditId,
        );
      }

      const options = thirdOptions(ctx);
      const estimate = finishChoice(
        'mainEffortThird', 'jev', restrictTo(answer.probabilities, options), options, started,
      );
      if (auditId !== undefined) {
        closeEstimateRow(auditId, {
          provider: 'jev', latencyMs: res.latencyMs, confidence: estimate.confidence, calibrated: estimate.calibrated,
        });
      }
      return { ...estimate, latencyMs: res.latencyMs };
    },

    async contactRole(ctx, contact) {
      const started = now();
      const state = buildJevState(ctx);
      const label = contactLabel(contact.enemyId);

      // The contact must be in the state for the question to point at it. It is dropped only when the
      // picture holds more contacts than the state carries, in which case there is nothing to ask about.
      if (!state.enemy_contacts.some((c) => c.contact === label)) {
        return fallback(
          () => heuristicEstimator.contactRole(ctx, contact),
          'jev: contact not in the state window',
          undefined,
        );
      }

      const key = stateKey(ctx);
      const auditId = withAudit ? openEstimateRow('contactRole', key.length, label) : undefined;

      const res = await call(
        state,
        { [CONTACT_ROLE_QUESTION_ID]: contactRoleQuestion(ctx, contact.enemyId) },
        { question: 'contactRole', ref: label, cacheKey: `role:${contact.enemyId}\u0000${key}` },
      );
      const answer = res.ok ? res.answers[CONTACT_ROLE_QUESTION_ID] : undefined;
      if (!answer || answer.type !== 'choice') {
        return fallback(
          () => heuristicEstimator.contactRole(ctx, contact),
          res.error ?? 'jev: no usable contact-role answer',
          auditId,
        );
      }

      const estimate = finishChoice(
        'contactRole', 'jev', restrictTo(answer.probabilities, CONTACT_ROLES), CONTACT_ROLES, started,
      );
      if (auditId !== undefined) {
        closeEstimateRow(auditId, {
          provider: 'jev', latencyMs: res.latencyMs, confidence: estimate.confidence, calibrated: estimate.calibrated,
        });
      }
      return { ...estimate, latencyMs: res.latencyMs };
    },

    async reserveCommitSoon(ctx) {
      const started = now();
      const state = buildJevState(ctx);
      const questions = pictureQuestions(ctx);
      const key = stateKey(ctx);
      const auditId = withAudit
        ? openEstimateRow('reserveCommitSoon', key.length, `H+${Math.round(ctx.elapsedMin)} picture`)
        : undefined;

      const res = await call(state, questions, {
        question: 'reserveCommitSoon',
        ref: `H+${Math.round(ctx.elapsedMin)}`,
        cacheKey: `picture\u0000${key}`,
      });
      const answer = res.ok ? res.answers[RESERVE_QUESTION_ID] : undefined;
      if (!answer || answer.type !== 'noul' || answer.noul === undefined) {
        return fallback(
          () => heuristicEstimator.reserveCommitSoon(ctx),
          res.error ?? 'jev: no usable reserve answer',
          auditId,
        );
      }

      const estimate = finishNoul('reserveCommitSoon', 'jev', answer.noul, started);
      if (auditId !== undefined) {
        closeEstimateRow(auditId, {
          provider: 'jev', latencyMs: res.latencyMs, confidence: estimate.confidence, calibrated: estimate.calibrated,
        });
      }
      return { ...estimate, latencyMs: res.latencyMs };
    },
  };
}

/** The shared Jev provider. Built once; it holds no per-fight state (the request cache lives in
 * `src/ai/jevClient.ts` and is cleared between fights). */
export const jevEstimator: Estimator = createJevEstimator();

/**
 * The factory the interface calls.
 *
 * `dials` is typed loosely on purpose: `Dials` does not carry a `jev` field yet (the store is owned by
 * another work stream this week), so `getJevFlag` reads it if it is there and falls back to the
 * module-level flag and the `?jev=1` query override if it is not. See `src/ai/jevFlag.ts` for the one
 * line the store adds later.
 */
export function getEstimator(dials?: unknown): Estimator {
  return jevEnabled(dials) ? jevEstimator : heuristicEstimator;
}

/** A short label for the interface: "heuristic", "Jev, calibrated on 2,000 fights". */
export function estimateLabel(estimate: Estimate<unknown>, calibratedOnRuns: number): string {
  if (estimate.provider === 'heuristic') {
    return estimate.calibrated ? `heuristic, calibrated on ${calibratedOnRuns.toLocaleString()} fights` : 'heuristic';
  }
  return estimate.calibrated ? `Jev, calibrated on ${calibratedOnRuns.toLocaleString()} fights` : 'Jev, uncalibrated';
}

/** The confidence bands the rest of the interface already speaks in. TypeSafe's Confidence page
 * recommends three tiers whose thresholds are fitted to the data; these are the app's, and the
 * calibration harness is what would move them. */
export function confidenceBand(confidence: number): 'low' | 'medium' | 'high' {
  if (confidence >= 0.6) return 'high';
  if (confidence >= 0.3) return 'medium';
  return 'low';
}

/** Re-exported so a caller can build a distribution's confidence the same way every provider does. */
export { confidenceOf };
